import axios, { AxiosInstance, AxiosRequestConfig } from 'axios';
import {
  withAccountHeader,
  withAuthHeader,
  withRetries,
  withConfiguredBaseUrl,
  compose,
  withE2ERequestStubbing,
} from '../hooks/useService';
import { ServiceAuth } from '../hooks/useService/useService';
import urls from '../util/urls';
import { SurveyAdapter } from '../components/Surveys/Components/SurveyAdapters/types';

export type PreviewDataResponse = {
  questions?: fhir.Observation[];
  surveyAdapters?: fhir.DomainResource[];
  scoring?: fhir.Observation[];
};

export type PreviewDataParams = {
  questions?: true;
  surveyAdapters?: true;
  scoring?: true;
};

export type Links = {
  self: string;
  next?: string;
};

export type GetSurveysResponse = {
  items: fhir.Questionnaire[];
  links?: Links;
};

export type GetSurveyResponsesResponse = {
  items: fhir.QuestionnaireResponse[];
  links?: Links;
};

export type CreateSurveyImageResponse = {
  imageId: string;
  uploadUrl: string;
};

export type GetSurveyImageResponse = {
  downloadUrl: string;
};

export type SurveyTemplate = {
  name: string;
  description?: string;
  templateConfig: {
    template: string;
  };
};

export type AnswerHash = Record<string, fhir.QuestionnaireResponseItemAnswer[]>;
export type TemplateInputObject = {
  answers: AnswerHash;
  response: fhir.QuestionnaireResponse;
};

export type TestTemplateResponse = {
  inputObject: TemplateInputObject;
  result: string;
  error?: string;
};

export type TemplateResponse = Omit<TestTemplateResponse, 'inputObject'>;

export type SurveyTemplateWithId = SurveyTemplate & { templateId: string };

export type GetResponsesParams = {
  cohort?: string;
  patientId?: string;
  projectId: string;
  pageSize?: number;
  nextPageToken?: string;
  includeSubjectName?: boolean;
  includeSurveyName?: boolean;
  status?: 'in-progress' | 'completed';
};

interface GetSurveyInviteParams {
  projectId: string;
  groupId: string;
  surveyId: string;
}

export type AutopopulateRequest = {
  patientId: string;
  questions: {
    linkId: string;
    resourceType: string;
    queries: {
      queryField: string;
      queryValue: string;
    }[];
    valueField: string;
  }[];
};

export type AutopopulateResponse = { linkId: string; value: any }[];

export default class SurveyService {
  private getSurveyAPI: (auth: ServiceAuth) => AxiosInstance;

  constructor(options?: Partial<AxiosRequestConfig>) {
    this.getSurveyAPI = (auth: ServiceAuth) =>
      compose(
        withE2ERequestStubbing,
        withAccountHeader(auth.account),
        withAuthHeader(auth.authToken),
        withConfiguredBaseUrl(auth.config),
        withRetries(),
      )(axios.create(options));
  }

  getSurveys = (auth: ServiceAuth) => async (params: any) => {
    const { datasetId, ...restParams } = params;
    return this.getSurveyAPI(auth).get<GetSurveysResponse>(
      // @ts-expect-error This directive was added as part of a bulk-change to enable strictNullChecks.
      urls.api.survey.surveys(datasetId, null, restParams),
    );
  };

  getSurvey =
    (auth: ServiceAuth) => async (datasetId: string, surveyId: string) =>
      this.getSurveyAPI(auth).get<fhir.Questionnaire>(
        urls.api.survey.surveys(datasetId, surveyId, {}),
      );

  getPrimarySurveyQuestionnaires =
    (auth: ServiceAuth) =>
    async (datasetId: string, primarySurveyId: string, params: any = {}) =>
      this.getSurveyAPI(auth).get<GetSurveysResponse>(
        urls.api.survey.primarySurveyVersions(
          datasetId,
          primarySurveyId,
          params,
        ),
      );

  updateSurvey =
    (auth: ServiceAuth) =>
    async (datasetId: string, questionnaire: fhir.Questionnaire) =>
      this.getSurveyAPI(auth).put<fhir.Questionnaire>(
        urls.api.survey.surveys(datasetId, questionnaire.id),
        questionnaire,
      );

  createSurvey =
    (auth: ServiceAuth) =>
    async (datasetId: string, questionnaire: Partial<fhir.Questionnaire>) =>
      this.getSurveyAPI(auth).post<fhir.Questionnaire>(
        urls.api.survey.surveys(datasetId),
        questionnaire,
      );

  versionSurvey =
    (auth: ServiceAuth) =>
    async (datasetId: string, questionnaire: fhir.Questionnaire) =>
      this.getSurveyAPI(auth).post<fhir.Questionnaire>(
        // @ts-expect-error This directive was added as part of a bulk-change to enable strictNullChecks.
        urls.api.survey.versionSurvey(datasetId, questionnaire.id),
        questionnaire,
      );

  getSurveyAdapters =
    (auth: ServiceAuth) => async (dataSetId: string, surveyId: string) =>
      this.getSurveyAPI(auth).get<SurveyAdapter[]>(
        urls.api.survey.surveyAdapter(dataSetId, surveyId),
      );

  getGlobalAdapters =
    (auth: ServiceAuth) => async (dataSetId: string, surveyId: string) =>
      this.getSurveyAPI(auth).get<SurveyAdapter[]>(
        urls.api.survey.globalAdapters(dataSetId, surveyId),
      );

  deleteAdapter =
    (auth: ServiceAuth) =>
    async (dataSetId: string, surveyId: string, name: string) =>
      this.getSurveyAPI(auth).delete<void>(
        urls.api.survey.surveyAdapter(
          dataSetId,
          surveyId,
          encodeURIComponent(name),
        ),
      );

  createAdapter =
    (auth: ServiceAuth) =>
    async (
      dataSetId: string,
      primarySurveyId: string,
      surveyAdapter: SurveyAdapter,
    ) =>
      this.getSurveyAPI(auth).post<SurveyAdapter>(
        urls.api.survey.surveyAdapter(dataSetId, primarySurveyId),
        surveyAdapter,
      );

  updateAdapter =
    (auth: ServiceAuth) =>
    async (
      dataSetId: string,
      primarySurveyId: string,
      surveyAdapter: SurveyAdapter,
    ) =>
      this.getSurveyAPI(auth).put<SurveyAdapter>(
        urls.api.survey.surveyAdapter(
          dataSetId,
          primarySurveyId,
          encodeURIComponent(surveyAdapter.sortName),
        ),
        surveyAdapter,
      );

  getResponses = (auth: ServiceAuth) => async (params: any) => {
    const { projectId, ...restParams } = params;
    return this.getSurveyAPI(auth).get<GetSurveyResponsesResponse>(
      // @ts-expect-error This directive was added as part of a bulk-change to enable strictNullChecks.
      urls.api.survey.surveyResponses(projectId, null, restParams),
    );
  };

  getResponse =
    (auth: ServiceAuth) =>
    async (
      params: { projectId: string; responseId: string } & Record<string, any>,
    ) => {
      const { projectId, responseId, ...restParams } = params;
      return this.getSurveyAPI(auth).get<fhir.QuestionnaireResponse>(
        urls.api.survey.surveyResponses(projectId, responseId, restParams),
      );
    };

  updateResponse =
    (auth: ServiceAuth) =>
    async (projectId: string, response: fhir.QuestionnaireResponse) =>
      this.getSurveyAPI(auth).put<fhir.QuestionnaireResponse>(
        urls.api.survey.surveyResponses(projectId, response.id),
        response,
      );

  createResponse =
    (auth: ServiceAuth) =>
    async (
      projectId: string,
      subjectId: string,
      surveyId: string,
      response: fhir.QuestionnaireResponse,
      cohort?: string,
    ) => {
      return this.getSurveyAPI(auth).post<fhir.QuestionnaireResponse>(
        urls.api.surveys.responses({ project: projectId, params: { cohort } }),
        {
          subject: { id: subjectId },
          survey: surveyId,
          response,
        },
      );
    };

  assignSurvey =
    (auth: ServiceAuth) =>
    async (projectId: string, subjectId: string, surveyId: string) => {
      return this.getSurveyAPI(auth).post<fhir.QuestionnaireResponse>(
        urls.api.surveys.responses({ project: projectId }),
        {
          subject: { id: subjectId },
          survey: surveyId,
        },
      );
    };

  sendReminder =
    (auth: ServiceAuth) => async (projectId: string, surveyId: string) => {
      return this.getSurveyAPI(auth).post(
        urls.api.surveys.reminder({ surveyId, projectId }),
      );
    };

  getSurveyInvite =
    (auth: ServiceAuth) =>
    async ({ projectId, groupId, surveyId }: GetSurveyInviteParams) => {
      return this.getSurveyAPI(auth).post(
        urls.api.surveys.responses({
          project: projectId,
        }),
        {
          invite: { group: groupId },
          survey: surveyId,
        },
      );
    };

  previewSurveyData =
    (auth: ServiceAuth) =>
    async (
      projectId: string,
      response: fhir.QuestionnaireResponse,
      params: PreviewDataParams,
    ) =>
      this.getSurveyAPI(auth).post<PreviewDataResponse>(
        urls.api.surveys.previewData(projectId, params),
        response,
      );

  createSurveyImage =
    (auth: ServiceAuth) =>
    async (projectId: string, surveyId: string, contentType: string) =>
      this.getSurveyAPI(auth).post<CreateSurveyImageResponse>(
        urls.api.surveys.surveyImage(projectId, surveyId),
        { contentType },
      );

  getSurveyImage =
    (auth: ServiceAuth) =>
    async (projectId: string, surveyId: string, imageId: string) =>
      this.getSurveyAPI(auth).get<GetSurveyImageResponse>(
        urls.api.surveys.surveyImage(projectId, surveyId, imageId),
      );

  deleteSurveyImage =
    (auth: ServiceAuth) =>
    async (projectId: string, surveyId: string, imageId: string) =>
      this.getSurveyAPI(auth).delete<void>(
        urls.api.surveys.surveyImage(projectId, surveyId, imageId),
      );

  generateAutopopulateData =
    (auth: ServiceAuth) =>
    async (
      projectId: string,
      cohortId: string | undefined,
      request: AutopopulateRequest,
    ) =>
      this.getSurveyAPI(auth).post<AutopopulateResponse>(
        urls.api.surveys.autopopulate(projectId, cohortId),
        request,
      );

  testSurveyTemplate =
    (auth: ServiceAuth) =>
    async (
      projectId: string,
      surveyId: string,
      response: fhir.QuestionnaireResponse,
      template: string,
    ) =>
      this.getSurveyAPI(auth).post<TestTemplateResponse>(
        urls.api.surveys.testTemplate(projectId, surveyId),
        { response, template },
      );

  generateTemplate =
    (auth: ServiceAuth) =>
    async (
      projectId: string,
      surveyId: string,
      templateId: string,
      responseId: string,
    ) =>
      this.getSurveyAPI(auth).get<TemplateResponse>(
        urls.api.surveys.responseTemplate(
          projectId,
          surveyId,
          templateId,
          responseId,
        ),
      );

  listSurveyTemplates =
    (auth: ServiceAuth) => async (projectId: string, surveyId: string) =>
      this.getSurveyAPI(auth).get<SurveyTemplateWithId[]>(
        urls.api.surveys.templates(projectId, surveyId),
      );

  createSurveyTemplate =
    (auth: ServiceAuth) =>
    async (
      projectId: string,
      primarySurveyId: string,
      template: SurveyTemplate,
    ) =>
      this.getSurveyAPI(auth).post<SurveyTemplateWithId>(
        urls.api.surveys.templates(projectId, primarySurveyId),
        template,
      );

  updateSurveyTemplate =
    (auth: ServiceAuth) =>
    async (
      projectId: string,
      primarySurveyId: string,
      templateId: string,
      template: SurveyTemplate,
    ) =>
      this.getSurveyAPI(auth).put<SurveyTemplateWithId>(
        urls.api.surveys.templates(projectId, primarySurveyId, templateId),
        template,
      );

  deleteSurveyTemplate =
    (auth: ServiceAuth) =>
    async (projectId: string, primarySurveyId: string, templateId: string) =>
      this.getSurveyAPI(auth).delete(
        urls.api.surveys.templates(projectId, primarySurveyId, templateId),
      );
}
